import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNetwork } from '../contexts/NetworkContext';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { Receipt, Building2, Zap, Droplets, Flame, Wifi, FileText, Copy, Save, Calculator } from 'lucide-react';

// Catégories avec type et icône
const EXPENSE_CATEGORIES = {
    // Charges fixes
    LOYER: { label: 'Loyer', type: 'FIXE', icon: Building2, color: 'bg-blue-100 text-blue-700' },
    SALAIRES: { label: 'Salaires', type: 'FIXE', icon: Receipt, color: 'bg-green-100 text-green-700' },
    INTERNET: { label: 'Internet', type: 'FIXE', icon: Wifi, color: 'bg-purple-100 text-purple-700' },
    // Charges variables
    GAZ: { label: 'Gaz', type: 'VARIABLE', icon: Flame, color: 'bg-orange-100 text-orange-700' },
    EAU: { label: 'Eau', type: 'VARIABLE', icon: Droplets, color: 'bg-cyan-100 text-cyan-700' },
    ELECTRICITE: { label: 'Électricité', type: 'VARIABLE', icon: Zap, color: 'bg-yellow-100 text-yellow-700' },
    IMPOTS: { label: 'Impôts', type: 'VARIABLE', icon: FileText, color: 'bg-red-100 text-red-700' },
};

const getCurrentMonth = () => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

const formatMonthLabel = (month) => {
    const [year, m] = month.split('-');
    const months = {
        '01': 'Janvier', '02': 'Février', '03': 'Mars', '04': 'Avril',
        '05': 'Mai', '06': 'Juin', '07': 'Juillet', '08': 'Août',
        '09': 'Septembre', '10': 'Octobre', '11': 'Novembre', '12': 'Décembre'
    };
    return `${months[m] || m} ${year}`;
};

const formatCFA = (amount) => {
    if (!amount && amount !== 0) return '0 CFA';
    return `${parseInt(amount).toLocaleString('fr-FR')} CFA`;
};

const ExpensesPage = () => {
    const { api, hasPermission } = useAuth();
    const { isOnline, getOfflineMessage } = useNetwork();
    const [selectedMonth, setSelectedMonth] = useState(getCurrentMonth());
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    
    // State pour chaque catégorie: { amount: '', notes: '' }
    const [formData, setFormData] = useState({});
    const [originalData, setOriginalData] = useState({});
    
    // Permissions - Charges require online
    const canEdit = (hasPermission ? hasPermission('create_expense') || hasPermission('edit_expense') : false) && isOnline;
    const canView = hasPermission ? hasPermission('view_expenses') : false;

    useEffect(() => {
        if (canView) {
            fetchData();
        }
    }, [selectedMonth, canView]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const res = await api().get(`/expenses?month=${selectedMonth}`);
            const expenses = res.data || [];
            
            // Initialiser le formulaire avec les données existantes ou 0
            const newFormData = {};
            Object.keys(EXPENSE_CATEGORIES).forEach(cat => {
                const existing = expenses.find(e => e.category === cat);
                newFormData[cat] = {
                    id: existing?.id || null,
                    amount: existing?.amount_cfa?.toString() || '',
                    notes: existing?.notes || ''
                };
            });
            
            setFormData(newFormData);
            setOriginalData(JSON.parse(JSON.stringify(newFormData)));
        } catch (error) {
            console.error('Error fetching expenses:', error);
            // En cas d'erreur, initialiser avec des valeurs vides
            const emptyData = {};
            Object.keys(EXPENSE_CATEGORIES).forEach(cat => {
                emptyData[cat] = { id: null, amount: '', notes: '' };
            });
            setFormData(emptyData);
            setOriginalData(emptyData);
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (category, field, value) => {
        if (field === 'amount') {
            // Accepter uniquement les nombres >= 0
            value = value.replace(/[^0-9]/g, '');
        }
        setFormData(prev => ({
            ...prev,
            [category]: { ...prev[category], [field]: value }
        }));
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const promises = [];
            
            for (const [category, data] of Object.entries(formData)) {
                const amount = parseInt(data.amount) || 0;
                const catInfo = EXPENSE_CATEGORIES[category];
                
                // Si montant > 0, créer ou mettre à jour
                // Si montant = 0 et existe, supprimer (optionnel) ou laisser à 0
                if (amount >= 0) {
                    if (data.id) {
                        // Update
                        promises.push(
                            api().put(`/expenses/${data.id}`, {
                                month: selectedMonth,
                                category,
                                amount_cfa: amount,
                                expense_type: catInfo.type,
                                notes: data.notes
                            }).catch(err => {
                                console.error(`Error updating ${category}:`, err);
                                throw err;
                            })
                        );
                    } else if (amount > 0 || data.notes) {
                        // Create only if there's a value or note
                        promises.push(
                            api().post('/expenses', {
                                month: selectedMonth,
                                category,
                                amount_cfa: amount,
                                expense_type: catInfo.type,
                                notes: data.notes
                            }).catch(err => {
                                console.error(`Error creating ${category}:`, err);
                                throw err;
                            })
                        );
                    }
                }
            }
            
            await Promise.all(promises);
            toast.success('Charges enregistrées avec succès');
            fetchData(); // Refresh to get IDs
        } catch (error) {
            toast.error('Erreur lors de l\'enregistrement');
        } finally {
            setSaving(false);
        }
    };

    const handleDuplicatePreviousMonth = async () => {
        if (!window.confirm(`Copier les charges du mois précédent vers ${formatMonthLabel(selectedMonth)} ?`)) return;
        try {
            const res = await api().post(`/expenses/duplicate-from-previous?target_month=${selectedMonth}`);
            toast.success(res.data.message || 'Charges copiées');
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de la copie');
        }
    };

    // Calcul des totaux
    const calculateTotals = () => {
        let fixedTotal = 0;
        let variableTotal = 0;
        
        Object.entries(formData).forEach(([category, data]) => {
            const amount = parseInt(data.amount) || 0;
            if (EXPENSE_CATEGORIES[category].type === 'FIXE') {
                fixedTotal += amount;
            } else {
                variableTotal += amount;
            }
        });
        
        return { fixedTotal, variableTotal, total: fixedTotal + variableTotal };
    };

    // Generate month options
    const getMonthOptions = () => {
        const options = [];
        const now = new Date();
        for (let i = -2; i < 12; i++) {
            const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
            const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
            options.push({ value, label: formatMonthLabel(value) });
        }
        return options;
    };

    // Check if form has changes
    const hasChanges = JSON.stringify(formData) !== JSON.stringify(originalData);
    const totals = calculateTotals();

    if (!canView) {
        return (
            <div className="flex items-center justify-center h-64">
                <p className="text-muted-foreground">Accès non autorisé</p>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600"></div>
            </div>
        );
    }

    const renderExpenseField = (category) => {
        const catInfo = EXPENSE_CATEGORIES[category];
        const Icon = catInfo.icon;
        const data = formData[category] || { amount: '', notes: '' };
        
        return (
            <div key={category} className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg">
                <div className={`p-2 rounded-lg ${catInfo.color}`}>
                    <Icon className="w-5 h-5" />
                </div>
                <div className="flex-1 space-y-2">
                    <Label className="font-medium">{catInfo.label}</Label>
                    <div className="flex gap-3">
                        <div className="flex-1">
                            <Input
                                type="text"
                                inputMode="numeric"
                                value={data.amount}
                                onChange={(e) => handleInputChange(category, 'amount', e.target.value)}
                                placeholder="0"
                                disabled={!canEdit}
                                className="text-right font-mono"
                            />
                        </div>
                        <span className="flex items-center text-sm text-muted-foreground">CFA</span>
                    </div>
                    <Input
                        type="text"
                        value={data.notes}
                        onChange={(e) => handleInputChange(category, 'notes', e.target.value)}
                        placeholder="Note (optionnel)"
                        disabled={!canEdit}
                        className="text-sm"
                    />
                </div>
                <div className="text-right min-w-[100px]">
                    <p className="font-mono font-semibold text-sm">
                        {formatCFA(data.amount || 0)}
                    </p>
                </div>
            </div>
        );
    };

    const fixedCategories = Object.keys(EXPENSE_CATEGORIES).filter(c => EXPENSE_CATEGORIES[c].type === 'FIXE');
    const variableCategories = Object.keys(EXPENSE_CATEGORIES).filter(c => EXPENSE_CATEGORIES[c].type === 'VARIABLE');

    return (
        <div className="space-y-6" data-testid="expenses-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Charges Mensuelles</h1>
                    <p className="text-gray-500">Saisie des charges fixes et variables</p>
                </div>
                
                {/* Sélecteur de mois + Boutons */}
                <div className="flex items-center gap-3">
                    <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                        <SelectTrigger className="w-[180px]">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {getMonthOptions().map(opt => (
                                <SelectItem key={opt.value} value={opt.value}>
                                    {opt.label}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    
                    {canEdit && (
                        <Button 
                            variant="outline" 
                            onClick={handleDuplicatePreviousMonth}
                            data-testid="duplicate-btn"
                        >
                            <Copy className="w-4 h-4 mr-2" />
                            Copier mois précédent
                        </Button>
                    )}
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Formulaire de saisie */}
                <div className="lg:col-span-2 space-y-6">
                    {/* Charges Fixes */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2 text-lg">
                                <Building2 className="w-5 h-5 text-blue-600" />
                                Charges Fixes
                            </CardTitle>
                            <CardDescription>Loyer, salaires, abonnements</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            {fixedCategories.map(cat => renderExpenseField(cat))}
                        </CardContent>
                    </Card>

                    {/* Charges Variables */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2 text-lg">
                                <Zap className="w-5 h-5 text-yellow-600" />
                                Charges Variables
                            </CardTitle>
                            <CardDescription>Gaz, eau, électricité, impôts</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            {variableCategories.map(cat => renderExpenseField(cat))}
                        </CardContent>
                    </Card>

                    {/* Bouton Enregistrer */}
                    {canEdit && (
                        <div className="flex justify-end">
                            <Button 
                                onClick={handleSave} 
                                disabled={saving || !hasChanges}
                                size="lg"
                                data-testid="save-btn"
                            >
                                <Save className="w-4 h-4 mr-2" />
                                {saving ? 'Enregistrement...' : 'Enregistrer'}
                            </Button>
                        </div>
                    )}
                </div>

                {/* Résumé Totaux */}
                <div className="space-y-4">
                    <Card className="sticky top-4">
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <Calculator className="w-5 h-5" />
                                Résumé {formatMonthLabel(selectedMonth)}
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg">
                                <span className="text-blue-700 font-medium">Charges Fixes</span>
                                <span className="font-mono font-bold text-blue-700">
                                    {formatCFA(totals.fixedTotal)}
                                </span>
                            </div>
                            <div className="flex justify-between items-center p-3 bg-yellow-50 rounded-lg">
                                <span className="text-yellow-700 font-medium">Charges Variables</span>
                                <span className="font-mono font-bold text-yellow-700">
                                    {formatCFA(totals.variableTotal)}
                                </span>
                            </div>
                            <div className="border-t pt-4">
                                <div className="flex justify-between items-center p-4 bg-primary/10 rounded-lg">
                                    <span className="text-primary font-semibold">Total du Mois</span>
                                    <span className="font-mono font-bold text-xl text-primary">
                                        {formatCFA(totals.total)}
                                    </span>
                                </div>
                            </div>
                            
                            {!canEdit && (
                                <div className="p-3 bg-amber-50 rounded-lg">
                                    <p className="text-amber-700 text-sm text-center">
                                        Mode lecture seule
                                    </p>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </div>
            </div>
        </div>
    );
};

export default ExpensesPage;
